import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { Subscription, debounceTime } from 'rxjs';
import { MultiSelect } from 'primeng/multiselect';
import { TranslateService } from '@ngx-translate/core';

import { AreaTypeEnum } from '@commons/enums/area-type.enum';
import { AREA_COORDS_VALIDATOR } from '@commons/forms/validators/area-coords-validator.function';
import { Area } from '@commons/models/domain/Area';
import { AreaDrawerService, State as AreaDrawerState } from '@commons/services/map/drawer/area/area-drawer.service';
import { WebAccessService } from '@commons/services/web.access/web-access.service';
import { GetTagsListByFilterResponse } from '@commons/models/remote.response/GetTagsListByFilterResponse';
import { Tag } from '@commons/models/domain/Tag';
import { AreaService } from '@commons/services/area/area.service';
import { NotificationService } from '@commons/services/notification/notification.service';

@Component({
  selector: 'app-area-form',
  templateUrl: './area-form.component.html',
  styleUrls: ['./area-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaFormComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('multiSelectGroups') public multiSelectGroupsRef: MultiSelect;

  @Output() public back$: EventEmitter<void | Area> = new EventEmitter();
  @Output() public onSelectAreaType$: EventEmitter<AreaTypeEnum> = new EventEmitter();
  @Input() public area?: Area;
  @Input() public isEditMode: boolean;

  public form: FormGroup;
  public color: string = '#4196FF';
  public isLoadingTags: boolean = true;
  public isSavingForm: boolean;

  public groups: Tag[] = [];
  public selectedGroups: Tag[] = [];

  public readonly AreaTypeEnum = AreaTypeEnum;
  private readonly MINIMAL_THICKNESS_IN_METERS = 10;
  private readonly ComponentSubsc: Subscription = new Subscription();
  private readonly defaultAreaColor: string = '#4196FF';

  private multiSelectFilterValue: string;

  constructor(
    private areaDrawer: AreaDrawerService,
    private fb: FormBuilder,
    private zone: NgZone,
    private webAccess: WebAccessService,
    private changeDetRef: ChangeDetectorRef,
    private areaService: AreaService,
    private notificationService: NotificationService,
    private translate: TranslateService,
  ) {
    this.createForm();
  }

  ngOnInit(): void {

  }

  ngAfterViewInit(): void {
    this.onChangeDrawnArea();
    if (this.area) {
      this.color = this.area.Cl || this.defaultAreaColor;
      this.form.patchValue(this.area);
      this.showEditAreaInMap();
    }
    this.getTags();
  }

  ngOnDestroy(): void {
    this.ComponentSubsc.unsubscribe();
    this.onCancel();
  }

  private showEditAreaInMap(): void {
    this.drawArea();
    this.areaDrawer.fitBoundsToVisibleArea();
  }

  private onCancel(): void {
    this.areaDrawer.setArea(undefined);
    this.areaDrawer.remove();
    this.areaDrawer.areaDrawerState = AreaDrawerState.Default;
    this.areaDrawer.drawingDoneSubject.next();
  }

  public addNewGroup(): void {
    const ifExistTagWithThatName = !this.multiSelectFilterValue?.length || this.groups.find(item => item.L === this.multiSelectFilterValue);

    if (ifExistTagWithThatName) {
        alert(this.translate.instant('areas.error-group-name'));
        return;
    }

    const newGroup: Tag = {
        L: this.multiSelectFilterValue,
        Id: null as any,
        T: 0,
        C: 0,
    };
    this.addNewGroupToList(newGroup);
    this.multiSelectGroupsRef.resetFilter();
    this.multiSelectGroupsRef.hide();
  }

  private addNewGroupToList(newGroup: Tag): void {
    this.groups = [newGroup, ...this.groups];
    this.selectedGroups = [...this.selectedGroups, {  ...newGroup }];
    this.multiSelectGroupsRef.value = [...this.selectedGroups];
    this.form.get('Tg')?.setValue([...this.selectedGroups]);
  }

  public onFilterTags({ filter }: { filter: string }): void {
    this.multiSelectFilterValue = filter;
  }

  public isErrorVisible(controlName: string): boolean {
    const control = this.form.get(controlName) as FormControl;
    return control.invalid && control.touched;
  }

  public submit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.saveArea();
    }
  }

  public onChangeSelectedTagsValue(data: { value: Tag[] }): void {
    this.selectedGroups = [...data.value];
    this.form.get('Tg')?.setValue([...this.selectedGroups]);
  }

  public removeGroup(index: number): void {
    const selectedGroups = [...this.selectedGroups];
    selectedGroups.splice(index, 1);
    this.selectedGroups = [...selectedGroups];
    this.multiSelectGroupsRef.value = [...this.selectedGroups];
    this.form.get('Tg')?.setValue([...this.selectedGroups]);
  }

  public setAreaType(type: AreaTypeEnum): void {
    this.form.patchValue({ 
        Tp: type,
        Coords: [],
    });
    this.areaDrawer.areaDrawerState = AreaDrawerState.Default;
    this.drawArea();
    this.onSelectAreaType$.emit(type);
  }

  private saveArea(): void {
    this.isSavingForm = true;
    const sub = this.areaService.saveAreaDetails$({ 
      ...this.form.value,
      Cl: this.color,
    })
    .subscribe({
      next: (response) => {
        this.onSuccessSaveArea(response);
      },
      error: () => {
        this.onErrorSaveArea();
      }
    });
    this.ComponentSubsc.add(sub);
  }

  private onSuccessSaveArea(area: Area): void {
    this.notificationService.showSuccess(this.translate.instant('areas.save-area-message'));
    this.back$.emit(area);
  }

  private onErrorSaveArea(): void {
    this.notificationService.showError(this.translate.instant('areas.save-area-error'));
    this.isSavingForm = false;
  }

  private createForm(): void {
    this.form = this.fb.group({
      Id: [null],
      Nm: [null, Validators.required],
      Th: [this.MINIMAL_THICKNESS_IN_METERS, Validators.required],
      Tp: [null, Validators.required],
      Coords: [[], AREA_COORDS_VALIDATOR],
      Tg: [[]],
      Cl: [null],
    });
  }

  public onChangeDrawnArea(): void {
    this.zone.run(() => {
        const sub = this.areaDrawer.drawnArea$.pipe(debounceTime(200)).subscribe(area => {
            this.area = area;
            this.form.patchValue(this.area, { emitEvent: false });
            this.form.markAsDirty();
            this.changeDetRef.markForCheck();
        });
        this.ComponentSubsc.add(sub);
    });
  }

  private drawArea(value: Area = this.form.value): void {
    this.areaDrawer.remove();
    this.areaDrawer.setArea(value as Area, true);
    this.areaDrawer.draw();
  }

  private getTags(): void {
    this.isLoadingTags = true;
    const body = {
        Phrase: '',
        TagType: 2,
    };
    const sub = this.webAccess.GetTagsListByFilter(body).subscribe(
        (data) => {
            this.setTags(data);
        }
    );
    this.ComponentSubsc.add(sub);
  }

  private setTags(data: GetTagsListByFilterResponse): void {
    this.zone.runOutsideAngular(() => {
        this.groups = [...data.Result].map((item, index) => ({ ...item, LastUsed: true, Index: index }));
        this.groups.sort((x, y) => x.L.localeCompare(y.L));
        this.selectGroupsOnInit();
        this.isLoadingTags = false;
        this.changeDetRef.markForCheck();
        this.changeDetRef.detectChanges();
        this.zone.run(() => {});
    });
  }

  private selectGroupsOnInit(): void {
    if (this.isEditMode && this.area?.Tg.length) {
        const selectedGroupsName: string[] = [...this.area.Tg].map(item => item.L);
        const groupsToSelected = this.groups.filter(item => selectedGroupsName.includes(item.L));
        this.selectedGroups = [...groupsToSelected];
        this.multiSelectGroupsRef.value = [...this.selectedGroups];
        this.form.get('Tg')?.setValue([...this.selectedGroups]);
    }
  }

}
